import { TextField } from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import NumberFormat, { NumberFormatProps } from "react-number-format";

export interface NumberFormatGFLProps extends NumberFormatProps {
  ref?: React.Ref<any>
}

// Converts a real-world currency value (e.g. 21.00) to internal integer cents (2100)
const toCents = (val: number): number => Math.round(val * 100);

const CurrencyInput = ({ value, onValueChange, disabled, color, ref, autoFocus }: NumberFormatGFLProps) => {
  // internalCents stores the value in cents as an integer (e.g. R$21,00 = 2100)
  const [internalCents, setInternalCents] = useState<number>(0);
  const internalCentsRef = useRef<number>(0);
  const isProgrammaticChange = useRef(false);

  // Sync external value prop into internal state
  useEffect(() => {
    const externalCents = (typeof value === "number") ? toCents(value) : 0;

    if (externalCents !== internalCentsRef.current) {
      isProgrammaticChange.current = true;
      internalCentsRef.current = externalCents;
      setInternalCents(externalCents);
    }
  }, [value])

  const handleChange = useCallback((v: any) => {
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }

    const parsed = parseFloat(v.value);
    if (isNaN(parsed)) {
      internalCentsRef.current = 0;
      setInternalCents(0);
      if (onValueChange) {
        onValueChange({ ...v, floatValue: 0 }, v.value);
      }
      return;
    }

    // NumberFormat value is in cents, so parsed is already cents
    const newCents = Math.round(parsed);
    internalCentsRef.current = newCents;
    setInternalCents(newCents);
    if (onValueChange) {
      onValueChange({ ...v, floatValue: newCents / 100 }, v.value);
    }
  }, [onValueChange]);

  const currencyFormatter = (formatted_value: any) => {
    if (!Number(formatted_value)) return "R$ 0,00";
    const reais = (formatted_value / 100) as number;
    const parts = reais.toFixed(2).split(".");
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `R$ ${intPart},${parts[1]}`;
  };

  const keyDown = useCallback((e: any) => {
    if (e.code === "Backspace" && internalCentsRef.current < 10) {
      e.preventDefault();
      internalCentsRef.current = 0;
      setInternalCents(0);
    }
  }, []);

  return (
    <NumberFormat
      ref={ref}
      autoFocus={autoFocus}
      value={internalCents}
      format={currencyFormatter}
      onValueChange={handleChange}
      prefix={"R$ "}
      allowEmptyFormatting
      decimalSeparator=","
      thousandSeparator="."
      decimalScale={2}
      customInput={TextField}
      onKeyDown={keyDown}
      disabled={disabled}
      color={color !== undefined ? 'warning' : undefined}
      focused={color === 'warning'}
    />
  );
};

export default CurrencyInput;